import axios from 'axios';
import AthenaService from './athenaService';
import FeedbackService from './feedbackService';
import emailjs from '@emailjs/browser';
import TwilioService from './twilioService';

// Initialize EmailJS with your public key
// Replace with your actual public key from the EmailJS dashboard
emailjs.init("JMSoqt0Be7WKqqfIo");

class AppointmentFeedbackService {
  constructor() {
    this.isMonitoring = false;
    this.monitoringInterval = null;
    this.processedAppointments = new Set();
    this.feedbackURL = window.location.origin + '/feedback';
  }

  /**
   * Subscribe to the CheckOut event in Athena Health API
   * @returns {Promise<Object>} Response from the subscription call
   */
  async subscribeToCheckOutEvents() {
    try {
      console.log('Subscribing to CheckOut events in Athena Health API...');
      
      const endpoint = 'appointments/changed/subscription';
      const params = {
        eventname: 'CheckOut'
      };
      
      const response = await AthenaService.makeRequest(endpoint, 'POST', params);
      console.log('Successfully subscribed to CheckOut events:', response);
      
      return response;
    } catch (error) {
      console.error('Failed to subscribe to CheckOut events:', error);
      throw new Error('Failed to subscribe to CheckOut events: ' + 
        (error.response?.data?.error || error.message));
    }
  }

  /**
   * Start monitoring for appointment changes, looking for CheckOut events
   * @param {number} intervalMinutes How often to check for changes (in minutes)
   */
  startMonitoring(intervalMinutes = 5) {
    if (this.isMonitoring) {
      console.log('Already monitoring for appointment changes');
      return;
    }

    console.log(`Starting to monitor for checked-out appointments every ${intervalMinutes} minutes`);
    this.isMonitoring = true;
    
    // Initial check
    this.checkForChangedAppointments();
    
    // Set up interval for regular checks
    const intervalMs = 5 * 30 * 1000;
    this.monitoringInterval = setInterval(() => {
      this.checkForChangedAppointments();
    }, intervalMs);
  }

  /**
   * Stop monitoring for appointment changes
   */
  stopMonitoring() {
    if (!this.isMonitoring) return;
    
    console.log('Stopping appointment change monitoring');
    clearInterval(this.monitoringInterval);
    this.monitoringInterval = null;
    this.isMonitoring = false;
  }

  /**
   * Check for changed appointments, focusing on checkouts
   */
  async checkForChangedAppointments() {
    try {
      console.log('Checking for changed appointments...');
      
      const endpoint = 'appointments/changed';
      const params = {
        showpatientdetail: true, // Include patient information
        limit: 100 // Limit results
      };
      
      const response = await AthenaService.makeRequest(endpoint, 'GET', params);
      console.log('Raw API response:', response); // Log the raw response for debugging
      
      // Handle the nested appointments array structure
      let appointmentsArray = [];
      
      if (response) {
        // Check all possible response formats
        if (Array.isArray(response)) {
          // Response is directly an array of appointments
          appointmentsArray = response;
          console.log('Response is an array with', appointmentsArray.length, 'appointments');
        } else if (response.appointments && Array.isArray(response.appointments)) {
          // Response has appointments array inside it
          appointmentsArray = response.appointments;
          console.log(`Found ${response.totalcount || appointmentsArray.length} total appointments in response.appointments`);
        } else if (typeof response === 'object') {
          // Try to find appointments array in any property
          for (const key in response) {
            if (Array.isArray(response[key])) {
              console.log(`Found array in response.${key} with ${response[key].length} items`);
              if (response[key].length > 0 && response[key][0].appointmentid) {
                appointmentsArray = response[key];
                console.log(`Using response.${key} as appointments array`);
                break;
              }
            }
          }
        }
      }
      
      if (!appointmentsArray || appointmentsArray.length === 0) {
        console.log('No changed appointments found in the response');
        return;
      }
      
      console.log(`Processing ${appointmentsArray.length} changed appointments`);
      
      // Filter for checked out appointments
      // Use checkoutdatetime field to determine if an appointment has been checked out
      const checkedOutAppointments = appointmentsArray.filter(appointment => {
        // Check if appointment has been checked out and not already processed
        const isCheckedOut = appointment.checkoutdatetime && appointment.checkoutdatetime.trim() !== '';
        const notProcessed = !this.processedAppointments.has(appointment.appointmentid);
        
        if (isCheckedOut && notProcessed) {
          console.log(`Found checked out appointment: ${appointment.appointmentid}, checkout time: ${appointment.checkoutdatetime}`);
          return true;
        }
        return false;
      });
      
      console.log(`Found ${checkedOutAppointments.length} newly checked out appointments`);
      
      // Process each checked out appointment
      for (const appointment of checkedOutAppointments) {
        await this.sendFeedbackLinkForAppointment(appointment);
        this.processedAppointments.add(appointment.appointmentid);
      }
      
      // Limit the size of processed appointments set to prevent memory leaks
      if (this.processedAppointments.size > 1000) {
        console.log('Trimming processed appointments cache');
        this.processedAppointments = new Set(
          Array.from(this.processedAppointments).slice(-500)
        );
      }
    } catch (error) {
      console.error('Error checking for changed appointments:', error);
    }
  }

  /**
   * Send a feedback link email to a patient who has checked out
   * @param {Object} appointment The appointment data
   */
  async sendFeedbackLinkForAppointment(appointment) {
    try {
      // Validate appointment data
      if (!appointment.patient) {
        console.warn('Cannot send feedback link: No patient data for appointment', appointment.appointmentid);
        return;
      }
      
      const patientName = appointment.patient.firstname;
      const patientEmail = appointment.patient.email;
      const patientPhone = appointment.patient.mobilephone || appointment.patient.homephone;
      
      // Track which methods were successfully used
      const sentVia = [];
      
      // Get provider name based on ID
      let providerName = "Your Provider";
      const providerId = appointment.providerid;
      
      // Special case for provider ID 30 - use IV Hydration as the name
      if (providerId === 30 || providerId === "30") {
        providerName = " for IV Hydration";
      } else if (providerId) {
        try {
          // Get all providers and find the matching one
          const providersResponse = await AthenaService.getAllProviders();
          
          // Extract the providers array from the response
          let providersArray = [];
          
          if (Array.isArray(providersResponse)) {
            // Response is directly an array of providers
            providersArray = providersResponse;
          } else if (providersResponse && providersResponse.providers && Array.isArray(providersResponse.providers)) {
            // Response has providers array inside it
            providersArray = providersResponse.providers;
          }
          
          if (providersArray.length > 0) {
            // Find the provider with matching ID
            const provider = providersArray.find(p => 
              p.providerid === providerId || 
              p.providerid === parseInt(providerId) ||
              p.providerid?.toString() === providerId?.toString()
            );
            
            if (provider && provider.firstname && provider.lastname) {
              providerName = ` with Dr. ${provider.firstname} ${provider.lastname}`;
            } else {
              console.warn(`Could not find provider with ID: ${providerId} in the providers list`);
            }
          } else {
            console.warn('Providers list is empty');
          }
        } catch (error) {
          console.warn(`Error fetching providers: ${error.message}`);
        }
      }
      
      // Create a feedback URL with provider ID and patient name parameters
      let feedbackURLWithParams = this.feedbackURL;
      const params = new URLSearchParams();
      
      if (providerId) {
        params.append('providerId', providerId);
      }
      
      if (patientName) {
        params.append('patientName', encodeURIComponent(patientName));
      }
      
      // Add department ID to the feedback URL parameters if available
      const departmentId = appointment.departmentid;
      if (departmentId) {
        params.append('departmentId', departmentId);
        console.log(`Adding department ID ${departmentId} to feedback URL`);
      }
      
      if (params.toString()) {
        feedbackURLWithParams += `?${params.toString()}`;
      }
      
      // Try to send email first if available
      if (patientEmail) {
        try {
          console.log(`Sending feedback link email to ${patientName} (${patientEmail}) for appointment with ${providerName}`);
          
          // Send email using emailjs
          await emailjs.send(
            'tebra', // Service ID
            'template_topdt6f', // Template ID
            {
              to_email: patientEmail,
              to_name: patientName,
              subject: 'Help Us Serve You Better - 2-Minute Feedback Request',
              feedback_link: feedbackURLWithParams,
              appointment_date: new Date(appointment.date).toLocaleDateString(),
              provider_name: providerName
            }
          );
          
          console.log(`Feedback link email sent successfully to ${patientEmail}`);
          sentVia.push('email');
        } catch (emailError) {
          console.error('Error sending feedback email:', emailError);
        }
      }
      
      // Try to send SMS if phone number is available
      if (patientPhone) {
        try {
          console.log(`Attempting to send feedback SMS to ${patientName} (${patientPhone})`);
          
          // Send SMS via TwilioService with the provider-specific URL
          const smsResult = await TwilioService.sendFeedbackSMS(
            appointment,
            patientPhone,
            patientName,
            providerName,
            feedbackURLWithParams // Pass the URL with parameters
          );
          
          if (smsResult && smsResult.success) {
            console.log(`Feedback SMS sent successfully to ${patientPhone}`);
            sentVia.push('SMS');
          } else {
            console.warn('SMS sending returned unsuccessful response');
          }
        } catch (smsError) {
          console.error('Error sending feedback SMS:', smsError);
        }
      }
      
      // Log feedback request
      await FeedbackService.logFeedbackRequest({
        patientId: appointment.patientid,
        appointmentId: appointment.appointmentid,
        emailSent: sentVia.includes('email'),
        smsSent: sentVia.includes('SMS'),
        sentAt: new Date().toISOString()
      });
      
      // Return success info
      if (sentVia.length > 0) {
        console.log(`Successfully sent feedback request via ${sentVia.join(' and ')} for appointment ${appointment.appointmentid}`);
        return { success: true, sentVia };
      } else {
        console.warn(`Could not send feedback request via any channel for appointment ${appointment.appointmentid}`);
        return { success: false, reason: 'No available contact methods' };
      }
    } catch (error) {
      console.error('Error sending feedback link:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new AppointmentFeedbackService();
